<template>
  <div class="drawing-history-modal">
    <div class="modal-header">
      <h4 class="modal-title">
        <tkd-icon name="history"/>
        Історія жеребкування
      </h4>
      <button type="button" class="close" @click="$emit('close')">
        <span>&times;</span>
      </button>
    </div>

    <div class="modal-body">
      <div v-if="!history || history.length === 0" class="no-history">
        <tkd-icon name="history" style="font-size: 48px; color: #ccc;"/>
        <p>Історія змін відсутня</p>
      </div>

      <div v-else class="history-list">
        <div 
          v-for="(entry, index) in sortedHistory" 
          :key="index"
          class="history-item"
          :class="{'history-item-current': index === 0}">
          
          <div class="history-header">
            <div class="history-version">
              <span class="version-badge" :class="{'badge-primary': index === 0, 'badge-secondary': index > 0}">
                Версія {{ entry.version }}
              </span>
              <span v-if="index === 0" class="current-label">ПОТОЧНА</span>
            </div>
            <div class="history-timestamp">
              {{ formatTimestamp(entry.timestamp) }}
            </div>
          </div>

          <div class="history-content">
            <div class="history-description">
              <tkd-icon :name="getActionIcon(entry.action)"/>
              <strong>{{ entry.description }}</strong>
            </div>
            
            <!-- Change Details -->
            <div class="history-change-details" v-if="entry.changeDetails">
              <div class="change-summary">
                <tkd-icon name="reset" style="font-size: 12px; color: #51cbce;"/>
                <span class="change-text">{{ getChangeSummary(entry.changeDetails) }}</span>
              </div>
              <div class="change-detailed" v-if="hasDetailedChanges(entry.changeDetails)" v-html="getDetailedChanges(entry.changeDetails)"></div>
            </div>
            
            <div class="history-details" v-if="entry.sportsmansCount">
              <span class="detail-item">
                <tkd-icon name="user"/>
                Спортсменів: {{ entry.sportsmansCount }}
              </span>
              <span class="detail-item" v-if="entry.inBracketCount !== undefined">
                <tkd-icon name="list"/>
                В сітці: {{ entry.inBracketCount }}
              </span>
            </div>
          </div>

          <div class="history-actions" v-if="index > 0">
            <button 
              class="btn btn-sm btn-info btn-fill"
              @click="viewVersion(entry)"
              title="Переглянути деталі">
              <tkd-icon name="search"/>
              Переглянути
            </button>
            <button 
              class="btn btn-sm btn-warning btn-fill"
              @click="restoreVersion(entry)"
              title="Відновити цю версію">
              <tkd-icon name="arrow-right"/>
              Відновити
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn btn-danger" @click="clearHistory" v-if="history && history.length > 0">
        <tkd-icon name="close"/>
        Очистити історію
      </button>
      <button class="btn btn-default" @click="$emit('close')">
        Закрити
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DrawingHistoryModal',
  props: {
    categoryId: {
      type: [Number, String],
      required: true
    }
  },
  computed: {
    history() {
      return this.$store.getters['tournament/getDrawingHistory'](this.categoryId);
    },
    sortedHistory() {
      // Sort by version descending (newest first)
      return [...this.history].sort((a, b) => b.version - a.version);
    }
  },
  mounted() {
    // Load history from localStorage
    this.$store.dispatch('tournament/loadDrawingHistory');
  },
  methods: {
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleString('uk-UA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getActionIcon(action) {
      const icons = {
        'manual_drawing': 'hand',
        'auto_drawing': 'dice',
        'bracket_reset': 'reset',
        'sportsman_added': 'plus',
        'sportsman_removed': 'trash',
        'fight_updated': 'edit',
        'draft_save': 'save'
      };
      return icons[action] || 'list';
    },

    getChangeSummary(changeDetails) {
      // Handle both old string format and new object format
      if (typeof changeDetails === 'string') {
        return changeDetails;
      }
      return changeDetails.summary || 'Без змін';
    },

    hasDetailedChanges(changeDetails) {
      // Check if we have detailed changes to show
      if (typeof changeDetails === 'string') {
        return false;
      }
      return changeDetails.detailed && changeDetails.detailed !== 'Без змін';
    },

    getDetailedChanges(changeDetails) {
      // Get the detailed HTML string
      if (typeof changeDetails === 'string') {
        return '';
      }
      return changeDetails.detailed || '';
    },

    viewVersion(entry) {
      this.$emit('view-version', entry);
    },

    restoreVersion(entry) {
      if (window.confirm(`Відновити версію ${entry.version}? Поточні зміни будуть збережені в історії.`)) {
        this.$emit('restore-version', entry);
      }
    },

    clearHistory() {
      if (window.confirm('Очистити всю історію змін для цієї категорії? Цю дію не можна скасувати.')) {
        this.$store.dispatch('tournament/clearDrawingHistory', this.categoryId);
        this.$notify({
          text: 'Історія очищена',
          horizontalAlign: 'right',
          verticalAlign: 'top',
          type: 'success'
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.drawing-history-modal {
  background: white;
  border-radius: 8px;
  max-width: 700px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;

  .modal-header {
    padding: 20px;
    border-bottom: 1px solid #e3e3e3;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .modal-title {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .close {
      background: none;
      border: none;
      font-size: 28px;
      cursor: pointer;
      opacity: 0.5;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;

    .no-history {
      text-align: center;
      padding: 60px 20px;
      color: #999;

      p {
        margin-top: 20px;
        font-size: 16px;
      }
    }

    .history-list {
      display: flex;
      flex-direction: column;
      gap: 15px;

      .history-item {
        border: 1px solid #e3e3e3;
        border-radius: 6px;
        padding: 15px;
        transition: all 0.2s;

        &:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        &.history-item-current {
          border-color: #51cbce;
          background: #f8fdfd;
        }

        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;

          .history-version {
            display: flex;
            align-items: center;
            gap: 10px;

            .version-badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: bold;

              &.badge-primary {
                background: #51cbce;
                color: white;
              }

              &.badge-secondary {
                background: #6c757d;
                color: white;
              }
            }

            .current-label {
              font-size: 10px;
              color: #51cbce;
              font-weight: bold;
              background: #e8f8f8;
              padding: 2px 8px;
              border-radius: 8px;
            }
          }

          .history-timestamp {
            font-size: 13px;
            color: #666;
          }
        }

        .history-content {
          margin-bottom: 12px;

          .history-description {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
            font-size: 14px;

            i, .tkd-icon {
              color: #51cbce;
            }
          }

          .history-change-details {
            margin-bottom: 8px;
            padding: 8px 12px;
            background: #f0f9fa;
            border-left: 3px solid #51cbce;
            border-radius: 4px;

            .change-summary {
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 13px;
              margin-bottom: 6px;

              .change-text {
                color: #333;
                font-weight: 500;
              }
            }

            .change-detailed {
              font-size: 12px;
              line-height: 1.6;
              color: #555;
              padding-left: 20px;
              margin-top: 6px;

              strong {
                color: #333;
                font-weight: 600;
              }
            }
          }

          .history-details {
            display: flex;
            gap: 15px;
            font-size: 13px;
            color: #666;

            .detail-item {
              display: flex;
              align-items: center;
              gap: 5px;

              i, .tkd-icon {
                font-size: 12px;
              }
            }
          }
        }

        .history-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;

          .btn {
            padding: 5px 12px;
            font-size: 12px;
            display: flex;
            align-items: center;
            gap: 5px;
          }
        }
      }
    }
  }

  .modal-footer {
    padding: 15px 20px;
    border-top: 1px solid #e3e3e3;
    display: flex;
    justify-content: space-between;

    .btn {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
}
</style>
